import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useResolutions } from '../../resolution/hooks/use-resolutions';
import { usePanels } from '../../shared/hooks/use-panels';
import { ResolutionConfig } from '../../shared/types/binding-types';
import { CanvasJSON } from '../../canvas/types';
import { BTN, C, FONT_UI } from '../../constants/design-tokens';

export const LoadJsonPanel: React.FC = () => {
  const { setResolutions, setActiveResolutionId } = useResolutions();
  const { isOpen, close } = usePanels();

  const [jsonText, setJsonText] = useState('');
  const [width, setWidth] = useState(1080);
  const [height, setHeight] = useState(1920);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen('loadJson')) return null;

  const handleLoad = () => {
    setError(null);

    if (!jsonText.trim()) {
      setError('Pegá un JSON.');
      return;
    }
    if (width <= 0 || height <= 0) {
      setError('Width y height deben ser mayores a 0.');
      return;
    }

    let parsed: CanvasJSON;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      setError('El JSON no es válido.');
      return;
    }
    if (!parsed || !Array.isArray(parsed.objects)) {
      setError('El JSON no parece un canvas de Fabric (falta `objects`).');
      return;
    }

    // Unique id per load so the standalone editor's "active resolution changed"
    // effect re-triggers even if the user loads another JSON in a row.
    const customRes: ResolutionConfig = {
      id: `custom-${Date.now()}`,
      label: 'Custom',
      canvasWidth: width,
      canvasHeight: height,
      type: 'video',
      layout: parsed
    };

    setResolutions([customRes]);
    setActiveResolutionId(customRes.id);
    close();
  };

  return createPortal(
    <>
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.35)',
          backdropFilter: 'blur(2px)',
          zIndex: 1000
        }}
      />
      <div
        data-panel-id='loadJson'
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 520,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          background: C.panelBg,
          border: `1px solid ${C.panelBorder}`,
          borderRadius: 14,
          padding: 20,
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
          fontFamily: FONT_UI,
          zIndex: 1001
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <h3 style={{ color: C.textPrimary, fontSize: 15, fontWeight: 600, margin: 0 }}>Cargar JSON</h3>
          <button
            onClick={close}
            style={{
              width: 28,
              height: 28,
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.textSecondary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 12, justifyContent: 'flex-start' }}>
          <div>
            <label style={{ display: 'block', color: C.textSecondary, fontSize: 12, marginBottom: 6 }}>Width</label>
            <input
              type='number'
              min={1}
              value={width}
              onChange={e => setWidth(Number(e.target.value))}
              style={{
                padding: '8px 10px',
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                color: C.textPrimary,
                fontSize: 13,
                outline: 'none'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', color: C.textSecondary, fontSize: 12, marginBottom: 6 }}>Height</label>
            <input
              type='number'
              min={1}
              value={height}
              onChange={e => setHeight(Number(e.target.value))}
              style={{
                padding: '8px 10px',
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                color: C.textPrimary,
                fontSize: 13,
                outline: 'none'
              }}
            />
          </div>
        </div>

        <label style={{ display: 'block', color: C.textSecondary, fontSize: 12, marginBottom: 6 }}>
          JSON de Fabric
        </label>
        <textarea
          value={jsonText}
          onChange={e => setJsonText(e.target.value)}
          placeholder='{"version": "7.1.0", "objects": [...] }'
          spellCheck={false}
          style={{
            flex: 1,
            minHeight: 220,
            padding: 10,
            fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
            fontSize: 12,
            color: C.textPrimary,
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            resize: 'vertical',
            outline: 'none',
            marginBottom: 12
          }}
        />

        {error && <p style={{ color: '#dc2626', fontSize: 12, margin: '0 0 12px' }}>{error}</p>}

        <button
          onClick={handleLoad}
          style={{
            width: '100%',
            padding: '10px 14px',
            background: BTN.primary.bg,
            border: 'none',
            borderRadius: 8,
            color: BTN.primary.text,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Cargar
        </button>
      </div>
    </>,
    document.body
  );
};
